/* Per-channel detail page — Profit & Balance Tracking, Monthly Performance,
   Strategy Details, Ledger, and the latest raw source message for one
   channel. Opened from ChannelsSection's row (channel name click), not a
   URL route — Dashboard.jsx has no router, so this is a view-state swap
   inside the existing "channels" tab, the same way ChannelEditDialog opens
   from a click rather than navigating anywhere. */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { channels, analytics, signals, trades as tradesApi } from "@/lib/api";
import { Badge, cn } from "@/components/atoms";
import { formatNum } from "@/pages/trades/TradesSection";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChatCircleText } from "@phosphor-icons/react";

const CLOSED_STATUSES = ["closed_by_signal", "closed_by_tp", "closed_by_sl", "closed_by_auto_roi"];
const ROW_GRADIENTS = [
  "linear-gradient(135deg,#4338CA,#6D5FF0)",
  "linear-gradient(135deg,#0F766E,#14B8A6)",
  "linear-gradient(135deg,#C2410C,#F59E0B)",
  "linear-gradient(135deg,#6D28D9,#A855F7)",
  "linear-gradient(135deg,#BE185D,#EC4899)",
  "linear-gradient(135deg,#1D4ED8,#38BDF8)",
];

/* Real strategy narrative, generated from the channel's own live
   risk_profile dict — one formatter covering every channel from the same
   code path, instead of hardcoding four different descriptions. Only
   describes fields that are actually set; silent on anything left at
   backend defaults so this never claims a behavior the channel doesn't
   actually have configured. */
function strategyBullets(rp) {
  const out = [];
  if (rp.position_sizing_mode === "margin" && rp.margin_percent_per_trade != null) {
    out.push({
      title: `${rp.default_leverage ?? rp.max_leverage ?? "?"}x, margin-mode sizing`,
      detail: `${rp.margin_percent_per_trade}% of balance committed as collateral per trade — not risk-normalized; dollar exposure scales with leverage.`,
    });
  } else if (rp.risk_percent_per_trade != null) {
    out.push({
      title: `${rp.default_leverage ?? rp.max_leverage ?? "?"}x, risk-mode sizing`,
      detail: `${rp.risk_percent_per_trade}% of balance risked per trade — dollar loss at SL is fixed regardless of stop width.`
        + (rp.max_sl_pct ? ` SL price-distance capped at ${rp.max_sl_pct}% (max_sl_pct) for capital efficiency.` : ""),
    });
  }
  if (rp.tp_waterfall_enabled) {
    out.push({
      title: "Provider TP waterfall",
      detail: `${rp.tp1_close_pct ?? 50}% closed at TP1, ${rp.tp2_close_pct ?? 30}% at TP2, remainder rides to TP3.`
        + (rp.sl_trail_to_be_at_tp1 ? " SL trails to breakeven at TP1" : "")
        + (rp.sl_trail_to_tp1_at_tp2 ? ", then to TP1's price at TP2." : rp.sl_trail_to_be_at_tp1 ? "." : ""),
    });
  }
  if (rp.auto_close_roi_pct) {
    out.push({
      title: `${rp.auto_close_roi_pct}% ROI safety-net close`,
      detail: "A flat auto-close backstops the exit strategy in case price never reaches the final target — full close at this ROI on margin.",
    });
  }
  out.push({
    title: `Up to ${rp.max_concurrent_positions || "unlimited"} concurrent positions`,
    detail: rp.skip_if_sl_beyond_liquidation
      ? "Wide-SL entries are skipped outright (skip_if_sl_beyond_liquidation on) rather than taken at a reduced size."
      : "Wide-SL entries are taken as-is (skip_if_sl_beyond_liquidation off) — sizing/SL caps handle risk instead of skipping.",
  });
  if (rp.skip_if_tp1_already_hit) {
    out.push({
      title: "Skip-if-already-past-TP1",
      detail: "Won't chase a signal if price already ran past TP1 by the time it's processed."
        + (rp.max_entry_slippage_pct ? ` Entry slippage tolerance capped at ${rp.max_entry_slippage_pct}%.` : ""),
    });
  }
  if (rp.virtual_balance_enabled) {
    out.push({
      title: "Virtual per-channel balance",
      detail: `Sizing compounds off its own tracked balance ($${rp.virtual_starting_balance ?? "?"} start, $${(rp.virtual_current_balance ?? 0).toFixed?.(2) ?? rp.virtual_current_balance} now) rather than the real shared exchange balance.`,
    });
  }
  if (rp.resting_limit_use_far_edge) {
    out.push({
      title: "Resting limit orders use the far edge of the zone",
      detail: "When price is outside a posted entry zone, the resting limit is placed at the far edge (long → high, short → low) instead of the near/best-price edge — fills sooner after a breakout instead of waiting for a deep retrace.",
    });
  }
  if (rp.allow_provider_sl_replace || rp.dedupe_window_seconds) {
    out.push({
      title: [rp.allow_provider_sl_replace && "Provider SL trusted", rp.dedupe_window_seconds && "dedupe on"].filter(Boolean).join(", "),
      detail: [
        rp.allow_provider_sl_replace && "Provider SL updates are allowed to replace the existing stop.",
        rp.dedupe_window_seconds && `Duplicate signals within a ${rp.dedupe_window_seconds}s window are ignored.`,
      ].filter(Boolean).join(" "),
    });
  }
  return out;
}

export default function ChannelDetailSection({ channelId, colorIndex = 0, onBack }) {
  const chQ = useQuery({ queryKey: ["channels"], queryFn: async () => (await channels.list()).data });
  const ch = (chQ.data || []).find((c) => c.id === channelId);
  const rp = ch?.risk_profile || {};

  const pnlQ = useQuery({ queryKey: ["per-channel-pnl"], queryFn: async () => (await analytics.perChannelPnl()).data });
  const pnl = (pnlQ.data || []).find((r) => r.channel_id === channelId);

  const monthlyQ = useQuery({
    queryKey: ["channel-monthly", channelId],
    queryFn: async () => (await analytics.channelMonthly(channelId)).data,
    enabled: !!channelId,
  });

  const ledgerQ = useQuery({
    queryKey: ["channel-ledger", channelId],
    queryFn: async () => {
      try {
        return { kind: "virtual", data: (await channels.virtualLedger(channelId)).data };
      } catch (e) {
        if (e?.response?.status !== 400) throw e;
        return { kind: "plain" };
      }
    },
    enabled: !!channelId,
  });

  const allTradesQ = useQuery({
    queryKey: ["trades", "all"],
    queryFn: async () => (await tradesApi.list()).data,
    enabled: ledgerQ.data?.kind === "plain",
  });

  const rawQ = useQuery({ queryKey: ["raw"], queryFn: async () => (await signals.listRaw()).data });

  if (!ch) {
    return (
      <div className="card-surface p-6 text-center text-sm text-slate-500">
        Loading channel…
      </div>
    );
  }

  // Ledger rows, either from the real virtual-balance reconstruction or a
  // plain running-PnL fallback for channels without virtual balance enabled.
  let ledgerRows = [];
  let ledgerMode = ledgerQ.data?.kind;
  if (ledgerMode === "virtual") {
    ledgerRows = (ledgerQ.data.data.ledger || []).slice().reverse();
  } else if (ledgerMode === "plain") {
    const closed = (allTradesQ.data || [])
      .filter((t) => t.channel_id === channelId && !t.paper_mode && CLOSED_STATUSES.includes(t.status))
      .sort((a, b) => new Date(a.closed_at) - new Date(b.closed_at));
    let running = 0;
    ledgerRows = closed.map((t) => {
      const before = running;
      running += t.realized_pnl || 0;
      return {
        trade_id: t.id, symbol: t.symbol, side: t.side, closed_at: t.closed_at,
        entry_price: t.entry_price, close_price: t.close_price, realized_pnl: t.realized_pnl,
        virtual_balance_before: before, virtual_balance_after: running,
        close_reason: humanStatus(t.status),
      };
    }).reverse();
  }

  const latestRaw = (rawQ.data || [])
    .filter((m) => m.channel_id === channelId)
    .sort((a, b) => new Date(b.received_at || b.created_at) - new Date(a.received_at || a.created_at))[0];

  const monthly = monthlyQ.data || [];
  const gradient = ROW_GRADIENTS[colorIndex % ROW_GRADIENTS.length];
  const isVirtual = ledgerMode === "virtual";
  const currentBalance = isVirtual ? ledgerQ.data.data.virtual_current_balance : null;
  const startBalance = isVirtual ? ledgerQ.data.data.virtual_starting_balance : null;
  const maxPl = Math.max(1, ...monthly.map((m) => Math.abs(m.realized_pnl)));
  const maxWL = Math.max(1, ...monthly.map((m) => Math.max(m.wins, m.losses)));

  return (
    <div className="space-y-4" data-testid="channel-detail-section">
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-900" data-testid="channel-detail-back">
        <ArrowLeft size={16} /> Channels
      </button>

      <div className="flex items-center gap-4">
        <div className="w-13 h-13 rounded-2xl shrink-0" style={{ background: gradient, width: 52, height: 52 }} />
        <div>
          <h2 className="font-display text-2xl">{ch.name}</h2>
          <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-2">
            {ch.exchange.toUpperCase()} · {rp.default_leverage ?? rp.max_leverage ?? "?"}x {rp.margin_mode || "cross"}
            <Badge variant={ch.enabled ? "profit" : "muted"}>{ch.enabled ? "● Active" : "Paused"}</Badge>
          </p>
        </div>
      </div>

      {/* Profit & Balance Tracking */}
      <div className="card-surface p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-display text-lg">Profit &amp; Balance Tracking</h3>
          <p className="text-xs text-slate-500">
            {isVirtual ? "Virtual channel balance" : "Real realized PnL — no virtual balance tracking for this channel"}
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {isVirtual && (
            <div>
              <p className="text-xs text-slate-500 font-semibold">Current balance</p>
              <p className="mono text-xl font-bold mt-1">${currentBalance?.toFixed(2)}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-slate-500 font-semibold">Realized PnL</p>
            <p className={cn("mono text-xl font-bold mt-1", (pnl?.realized_pnl ?? 0) >= 0 ? "text-emerald-600" : "text-red-600")}>
              {(pnl?.realized_pnl ?? 0) >= 0 ? "+" : "-"}${Math.abs(pnl?.realized_pnl ?? 0).toFixed(2)}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 font-semibold">Win rate</p>
            <p className="mono text-xl font-bold mt-1">{pnl && pnl.wins + pnl.losses > 0 ? `${pnl.win_rate_pct}%` : "—"}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 font-semibold">Trades closed</p>
            <p className="mono text-xl font-bold mt-1">{pnl?.trades_count ?? 0}</p>
          </div>
        </div>
        {isVirtual && startBalance != null && (
          <p className="text-xs text-slate-400 mt-3">${startBalance.toFixed(2)} start</p>
        )}
      </div>

      {/* Monthly Performance */}
      <div className="card-surface p-6">
        <h3 className="font-display text-lg">Monthly Performance</h3>
        {monthly.length === 0 ? (
          <p className="text-sm text-slate-500 mt-3">No closed trades yet for this channel.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div>
              <p className="text-xs font-bold text-slate-500 mb-3">P/L by month</p>
              <div className="flex items-end gap-4" style={{ height: 100 }}>
                {monthly.map((m) => (
                  <div key={m.month} className="flex flex-col items-center gap-1.5">
                    <span className={cn("mono text-[11px] font-bold", m.realized_pnl >= 0 ? "text-emerald-600" : "text-red-600")}>
                      {m.realized_pnl >= 0 ? "+" : "-"}${Math.abs(m.realized_pnl).toFixed(2)}
                    </span>
                    <div
                      className={cn("w-8 rounded-t", m.realized_pnl >= 0 ? "bg-emerald-500" : "bg-red-500")}
                      style={{ height: Math.max(4, (Math.abs(m.realized_pnl) / maxPl) * 70) }}
                    />
                    <span className="text-[11px] text-slate-400">{m.month}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-slate-500 mb-3">Win / loss by month</p>
              <div className="flex items-end gap-4" style={{ height: 100 }}>
                {monthly.map((m) => (
                  <div key={m.month} className="flex flex-col items-center gap-1.5">
                    <div className="flex items-end gap-1" style={{ height: 70 }}>
                      <div className="w-4 rounded-t bg-emerald-500 relative flex justify-center" style={{ height: Math.max(2, (m.wins / maxWL) * 70) }}>
                        <span className="mono text-[10px] font-bold text-emerald-600 absolute -top-4">{m.wins}</span>
                      </div>
                      <div className="w-4 rounded-t bg-red-500 relative flex justify-center" style={{ height: Math.max(2, (m.losses / maxWL) * 70) }}>
                        <span className="mono text-[10px] font-bold text-red-600 absolute -top-4">{m.losses}</span>
                      </div>
                    </div>
                    <span className="text-[11px] text-slate-400">{m.month}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Strategy Details */}
      <div className="card-surface p-6">
        <h3 className="font-display text-lg">Strategy Details</h3>
        <p className="text-xs text-slate-500 mt-1">Live risk_profile configuration</p>
        <div className="mt-3 divide-y divide-slate-100">
          {strategyBullets(rp).map((b, i) => (
            <div key={i} className="py-3 first:pt-0">
              <p className="text-sm font-bold">{b.title}</p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{b.detail}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Ledger */}
      <div className="card-surface p-0 overflow-hidden">
        <div className="p-6 pb-3">
          <h3 className="font-display text-lg">Ledger</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-slate-500 border-t border-b border-slate-200">
              <tr>
                <th className="text-left py-2.5 px-6">Symbol</th>
                <th className="text-left py-2.5 px-2">Side</th>
                <th className="text-right py-2.5 px-2">Entry</th>
                <th className="text-right py-2.5 px-2">Exit</th>
                <th className="text-right py-2.5 px-2">PnL</th>
                {isVirtual && <th className="text-right py-2.5 px-2">Running Bal.</th>}
                <th className="text-left py-2.5 px-6">Closed</th>
              </tr>
            </thead>
            <tbody>
              {ledgerRows.map((r) => (
                <tr key={r.trade_id} className="border-b border-slate-100 last:border-0">
                  <td className="py-3 px-6 font-semibold">{r.symbol}</td>
                  <td className="py-3 px-2"><Badge variant={r.side === "long" ? "profit" : "loss"}>{r.side}</Badge></td>
                  <td className="py-3 px-2 mono text-right">{formatNum(r.entry_price)}</td>
                  <td className="py-3 px-2 mono text-right">{formatNum(r.close_price)}</td>
                  <td className={cn("py-3 px-2 mono text-right font-bold", r.realized_pnl >= 0 ? "text-emerald-600" : "text-red-600")}>
                    {r.realized_pnl >= 0 ? "+" : "-"}${Math.abs(r.realized_pnl).toFixed(2)}
                  </td>
                  {isVirtual && <td className="py-3 px-2 mono text-right font-bold">${r.virtual_balance_after.toFixed(2)}</td>}
                  <td className="py-3 px-6 text-slate-500">{r.closed_at ? new Date(r.closed_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
              {ledgerRows.length === 0 && (
                <tr><td colSpan={isVirtual ? 7 : 6} className="py-8 text-center text-slate-500">No closed trades yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Latest raw source message */}
      <div className="card-surface p-6">
        <h3 className="font-display text-lg">Latest Raw Source Message</h3>
        <p className="text-xs text-slate-500 mt-1">Exactly as captured from the source, before parsing</p>
        {latestRaw ? (
          <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-200 flex gap-3">
            <ChatCircleText size={18} className="text-slate-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap">"{latestRaw.text}"</p>
              <p className="text-xs text-slate-400 mt-2">{new Date(latestRaw.received_at || latestRaw.created_at).toLocaleString()}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500 mt-3">No messages captured for this channel yet.</p>
        )}
      </div>
    </div>
  );
}

function humanStatus(s) {
  return (s || "").replace(/_/g, " ");
}
